/**
 * DOM-free extraction of LandXML TIN surfaces.
 *
 * WHY THIS EXISTS
 * ---------------
 * A civil-scale DTM export is mostly `<Pnts>` and `<Faces>`: a 206 MB LandXML
 * from Civil 3D is ~1.2 M `<P>` and ~2.4 M `<F>` elements, and everything else
 * in the file (units, alignments, parcels, pipe networks) fits in a couple of
 * kilobytes. Handing all of that to `DOMParser` costs ~6.7 s and hundreds of MB
 * of DOM nodes before a single vertex is read, and `querySelectorAll('P')` then
 * materialises a million-entry Element array on top.
 *
 * So we cut the two heavy blocks out of the raw text, scan them straight into
 * typed arrays, and hand the DOM only what is left — a document small enough to
 * parse instantly. Measured on the 206 MB fixture: 11.4 s → 0.95 s, 1.13 GB →
 * 393 MB peak heap, with byte-identical geometry.
 *
 * The extracted mesh is INDEXED (unique vertices + a `Uint32Array` of triangle
 * corners) rather than the triangle soup the DOM path builds. For that fixture
 * this is 1,186,586 vertices instead of 7,117,473 — a 14 MB position buffer
 * plus a 27 MB index instead of 85 MB of positions and 85 MB of normals.
 *
 * Anything this module cannot confidently handle is left alone: `extractTins`
 * returns the input text unchanged and no meshes, and the caller falls back to
 * the original DOM parser. It must never guess.
 *
 * Every scanner here takes `TextLike` rather than `string` — a plain `string`
 * already satisfies it, so nothing below changes for the common case — so a
 * document too large for a single JS string (see `chunkedText.ts`) can be
 * scanned too. Only tiny, bounded slices (a number token, an id attribute,
 * the `<Units>` body, the non-TIN "light" content) are ever materialised as
 * real strings.
 */
import type { TextLike } from './chunkedText';

/**
 * Attribute stamped onto the emptied `<Pnts>` / `<Faces>` markers in the light
 * document, so `parseSurfaces` can pair a `<Surface>` in the DOM with its
 * already-extracted mesh. Positional pairing would be fragile — the DOM parser
 * skips surfaces on conditions this scanner does not model.
 */
export const TIN_INDEX_ATTR = 'tinIndex';

export interface RawTin {
  /**
   * Unique vertices as [easting, northing, elevation] triples, ABSOLUTE
   * coords, in METRES — the file's declared `<Units>/linearUnit` must BE
   * metres or the read is refused (see `assertLandxmlMetres`).
   */
  positions: Float32Array;
  /**
   * The same vertices at full double precision, retained as the snapshot the
   * drape sampler needs. `LandXML.tsx` translates the Float32 geometry by
   * `-origin` right after parsing; this copy stays in absolute coordinates so a
   * sampler can still be built from it afterwards — including lazily, long
   * after the geometry has moved.
   */
  positions64: Float64Array;
  /** Triangle corners, 3 per triangle, indexing into `positions`. */
  index: Uint32Array;
  vertexCount: number;
  triangleCount: number;
  zMin: number;
  zMax: number;
  /** Summed 3D triangle area (m²), computed at double precision. */
  surfaceArea: number;
}

export interface TinExtraction {
  /** The document with every extracted `<Pnts>`/`<Faces>` body replaced by a marker. */
  lightText: string;
  /** Extracted meshes, positionally matching the `TIN_INDEX_ATTR` values. */
  tins: RawTin[];
}

const isWs = (c: number) => c === 32 || c === 10 || c === 9 || c === 13;

/** True when `text[at]` ends an element NAME (so `<P` doesn't match `<PntList3D`). */
const endsName = (text: TextLike, at: number) => {
  const c = text.charCodeAt(at);
  return c === 62 /* > */ || c === 47 /* / */ || isWs(c);
};

/**
 * Thrown by every LandXML reader that meets a `linearUnit` other than metres —
 * the fast TIN path here, and the DOM parsers in `landxml.ts`. It carries the
 * declared unit as data so the caller can raise a translated, user-facing
 * error (`LandXML.tsx`) instead of an English console line (corridor-trust
 * T5).
 */
export class LandxmlNonMetreUnitsError extends Error {
  /** The `linearUnit` string exactly as the file declared it. */
  readonly unit: string;
  constructor(unit: string) {
    super(
      `This LandXML declares linearUnit="${unit}". Non-metre LandXML files are not supported — ` +
        'convert the file to metres (linearUnit="meter") and re-import.',
    );
    this.name = 'LandxmlNonMetreUnitsError';
    this.unit = unit;
  }
}

/** The only `linearUnit` spellings this reader renders (compared lower-cased, trimmed). */
const METRE_UNITS = new Set(['meter', 'metre']);

/**
 * Refuse anything but metres. Scans raw LandXML text for the first `<Units>`
 * child carrying a `linearUnit` attribute (see `findDeclaredLinearUnit`) and:
 *  - no `<Units>` element, or none of its children declaring a `linearUnit`
 *    at all, keeps metres (with a console.warn) — every project's LandXML
 *    predates this check, and some synthetic/hand-built TINs never declare
 *    units;
 *  - `meter`/`metre` (any case, surrounding whitespace ignored) passes;
 *  - anything else — a foot, a kilometre, an unrecognised word, or an
 *    explicit but empty `linearUnit=""` — throws.
 *
 * Nothing is scaled any more. The reader used to convert a declared unit to
 * metres through a table mirrored from `scripts/surface_io.py`, but only for
 * TIN coordinates: curve radii, station equations, profile elevations, parcel
 * areas and pipe diameters were all left in the file's own unit, and the
 * server-side reader refuses non-metre files outright. A half-converted file
 * that looks plausible is worse than a refused one, and a client that scales
 * while the server refuses is worse still.
 *
 * Works on the raw text rather than a parsed DOM: `<Units>` is always a few
 * bytes near the top of the file, so this costs nothing next to the
 * multi-hundred-MB `<Pnts>`/`<Faces>` blocks this module exists to avoid
 * handing to DOMParser.
 */
export function assertLandxmlMetres(text: TextLike): void {
  const linearUnit = findDeclaredLinearUnit(text);
  if (linearUnit === null) {
    console.warn('LandXML: no <Units>/linearUnit found; assuming metres.');
    return;
  }
  if (!METRE_UNITS.has(linearUnit.trim().toLowerCase())) throw new LandxmlNonMetreUnitsError(linearUnit);
}

/** Element-opening-tag scanner, reused by `findDeclaredLinearUnit`. */
const OPEN_TAG_RE = /<([A-Za-z_][\w.:-]*)((?:\s+[^<>]*)?)\/?>/g;
const LINEAR_UNIT_ATTR_RE = /\blinearUnit\s*=\s*"([^"]*)"|\blinearUnit\s*=\s*'([^']*)'/;

/**
 * The first `linearUnit` attribute among `<Units>`'s children, verbatim, or
 * null when neither `<Units>` nor any of its children declares one. The single
 * place this module reads the attribute.
 *
 * Scans ALL of `<Units>`'s children, not just child[0] — a leading
 * `<Feature/>` before the `<Imperial>`/`<Metric>` element that actually
 * declares the unit is common in real LandXML, and stopping at child[0]
 * silently mistakes that leading child for "no unit declared", i.e. metres.
 */
function findDeclaredLinearUnit(text: TextLike): string | null {
  const openStart = text.indexOf('<Units');
  if (openStart < 0) return null;
  const nameEnd = openStart + '<Units'.length;
  if (!endsName(text, nameEnd)) return null; // e.g. <UnitsFoo>, not <Units>
  const gt = text.indexOf('>', nameEnd);
  if (gt < 0) return null;
  if (text.charCodeAt(gt - 1) === 47 /* / */) return null; // <Units/> — no children
  const bodyEnd = text.indexOf('</Units>', gt + 1);
  if (bodyEnd < 0) return null;
  const body = text.slice(gt + 1, bodyEnd);

  OPEN_TAG_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = OPEN_TAG_RE.exec(body)) !== null) {
    const attrMatch = LINEAR_UNIT_ATTR_RE.exec(m[2]);
    if (attrMatch) return attrMatch[1] ?? attrMatch[2] ?? '';
  }
  return null;
}

interface Block {
  openStart: number;
  bodyStart: number;
  bodyEnd: number;
  closeEnd: number;
}

/**
 * Locate every `<tag …>…</tag>` block at any depth. Self-closing `<tag/>` is
 * skipped (it has no body to extract) and so is any longer element name that
 * merely starts with `tag`.
 */
function findBlocks(text: TextLike, tag: string): Block[] {
  const open = `<${tag}`;
  const close = `</${tag}>`;
  const blocks: Block[] = [];
  let i = 0;
  for (;;) {
    const start = text.indexOf(open, i);
    if (start < 0) break;
    const nameEnd = start + open.length;
    if (!endsName(text, nameEnd)) {
      i = nameEnd;
      continue;
    }
    const gt = text.indexOf('>', nameEnd);
    if (gt < 0) break;
    if (text.charCodeAt(gt - 1) === 47 /* / */) {
      i = gt + 1; // self-closing — nothing to pull out
      continue;
    }
    const closeAt = text.indexOf(close, gt + 1);
    if (closeAt < 0) break;
    blocks.push({ openStart: start, bodyStart: gt + 1, bodyEnd: closeAt, closeEnd: closeAt + close.length });
    i = closeAt + close.length;
  }
  return blocks;
}

/**
 * Read up to `max` whitespace-separated numbers from `text[start,end)`.
 * Deliberately avoids `split(/\s+/).map(Number)`: at ~3.5 M calls that array
 * churn dominates the parse.
 */
function readNumbers(text: TextLike, start: number, end: number, out: Float64Array, max: number): number {
  let i = start;
  let n = 0;
  while (i < end && n < max) {
    while (i < end && isWs(text.charCodeAt(i))) i++;
    if (i >= end) break;
    const from = i;
    while (i < end && !isWs(text.charCodeAt(i))) i++;
    out[n++] = +text.slice(from, i);
  }
  return n;
}

/** Read the integer value of `id="…"` from an element's attribute span. */
function readIdAttr(text: TextLike, from: number, to: number): number {
  const at = text.indexOf('id="', from);
  if (at < 0 || at >= to) return -1;
  let i = at + 4;
  let v = 0;
  let any = false;
  while (i < to) {
    const c = text.charCodeAt(i);
    if (c < 48 || c > 57) break;
    v = v * 10 + (c - 48);
    any = true;
    i++;
  }
  return any ? v : -1;
}

const grow64 = (a: Float64Array, need: number) => {
  if (need <= a.length) return a;
  const next = new Float64Array(Math.max(need, a.length * 2));
  next.set(a);
  return next;
};
const grow32i = (a: Int32Array, need: number) => {
  if (need <= a.length) return a;
  const next = new Int32Array(Math.max(need, a.length * 2));
  next.set(a);
  return next;
};
const growU32 = (a: Uint32Array, need: number) => {
  if (need <= a.length) return a;
  const next = new Uint32Array(Math.max(need, a.length * 2));
  next.set(a);
  return next;
};

interface ParsedPoints {
  xyz: Float64Array;
  ids: Int32Array;
  count: number;
  maxId: number;
  zMin: number;
  zMax: number;
}

/**
 * Scan `<P id="n">northing easting elevation</P>` into flat arrays.
 * Coordinates come out as [easting, northing, elevation] — the same swap
 * `parsePoint` in landxml.ts applies.
 */
function parsePnts(text: TextLike, start: number, end: number): ParsedPoints {
  let xyz = new Float64Array(3 * 4096);
  let ids = new Int32Array(4096);
  let count = 0;
  let maxId = 0;
  let zMin = Infinity;
  let zMax = -Infinity;
  const vals = new Float64Array(3);
  let i = start;
  while (i < end) {
    const open = text.indexOf('<P', i);
    if (open < 0 || open >= end) break;
    const nameEnd = open + 2;
    if (!endsName(text, nameEnd)) {
      i = nameEnd;
      continue;
    }
    const gt = text.indexOf('>', nameEnd);
    if (gt < 0 || gt >= end) break;
    if (text.charCodeAt(gt - 1) === 47 /* / */) {
      i = gt + 1;
      continue;
    }
    const closeAt = text.indexOf('</P>', gt + 1);
    if (closeAt < 0 || closeAt > end) break;

    const id = readIdAttr(text, nameEnd, gt);
    vals[0] = 0;
    vals[1] = 0;
    vals[2] = 0;
    const got = readNumbers(text, gt + 1, closeAt, vals, 3);
    if (id >= 0 && got >= 2) {
      xyz = grow64(xyz, (count + 1) * 3);
      ids = grow32i(ids, count + 1);
      const z = got >= 3 ? vals[2] : 0;
      xyz[count * 3] = vals[1]; // easting  -> x
      xyz[count * 3 + 1] = vals[0]; // northing -> y
      xyz[count * 3 + 2] = z;
      ids[count] = id;
      if (id > maxId) maxId = id;
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
      count++;
    }
    i = closeAt + 4;
  }
  return { xyz, ids, count, maxId, zMin, zMax };
}

/**
 * Build the id → vertex-index lookup. Civil 3D numbers points densely, which a
 * flat `Int32Array` answers in one load; a sparse or huge id space falls back
 * to a Map rather than allocating a gigantic array.
 */
function buildIdLookup(ids: Int32Array, count: number, maxId: number): (id: number) => number {
  if (maxId >= 0 && maxId < count * 8 + 1024) {
    const dense = new Int32Array(maxId + 1).fill(-1);
    for (let i = 0; i < count; i++) dense[ids[i]] = i;
    return (id) => (id >= 0 && id <= maxId ? dense[id] : -1);
  }
  const sparse = new Map<number, number>();
  for (let i = 0; i < count; i++) sparse.set(ids[i], i);
  return (id) => sparse.get(id) ?? -1;
}

/** Scan `<F …>a b c</F>` into a triangle index buffer, dropping unresolvable faces. */
function parseFaces(text: TextLike, start: number, end: number, lookup: (id: number) => number): Uint32Array {
  let index = new Uint32Array(3 * 4096);
  let tris = 0;
  const vals = new Float64Array(3);
  let i = start;
  while (i < end) {
    const open = text.indexOf('<F', i);
    if (open < 0 || open >= end) break;
    const nameEnd = open + 2;
    if (!endsName(text, nameEnd)) {
      i = nameEnd;
      continue;
    }
    const gt = text.indexOf('>', nameEnd);
    if (gt < 0 || gt >= end) break;
    if (text.charCodeAt(gt - 1) === 47 /* / */) {
      i = gt + 1;
      continue;
    }
    const closeAt = text.indexOf('</F>', gt + 1);
    if (closeAt < 0 || closeAt > end) break;

    const got = readNumbers(text, gt + 1, closeAt, vals, 3);
    if (got >= 3) {
      const a = lookup(vals[0]);
      const b = lookup(vals[1]);
      const c = lookup(vals[2]);
      if (a >= 0 && b >= 0 && c >= 0) {
        index = growU32(index, (tris + 1) * 3);
        index[tris * 3] = a;
        index[tris * 3 + 1] = b;
        index[tris * 3 + 2] = c;
        tris++;
      }
    }
    i = closeAt + 4;
  }
  return index.subarray(0, tris * 3);
}

/** Summed 3D area of the indexed triangles, at double precision. */
function indexedArea(xyz: Float64Array, index: Uint32Array): number {
  let total = 0;
  for (let t = 0; t + 2 < index.length; t += 3) {
    const a = index[t] * 3;
    const b = index[t + 1] * 3;
    const c = index[t + 2] * 3;
    const abx = xyz[b] - xyz[a];
    const aby = xyz[b + 1] - xyz[a + 1];
    const abz = xyz[b + 2] - xyz[a + 2];
    const acx = xyz[c] - xyz[a];
    const acy = xyz[c + 1] - xyz[a + 1];
    const acz = xyz[c + 2] - xyz[a + 2];
    const cx = aby * acz - abz * acy;
    const cy = abz * acx - abx * acz;
    const cz = abx * acy - aby * acx;
    total += Math.sqrt(cx * cx + cy * cy + cz * cz) * 0.5;
  }
  return total;
}

/**
 * Cut every TIN out of a LandXML document's raw text.
 *
 * Returns the meshes plus a "light" copy of the document in which each
 * extracted `<Pnts>`/`<Faces>` pair has become `<Pnts tinIndex="n"/>` /
 * `<Faces tinIndex="n"/>` — cheap to hand to `DOMParser`, and still carrying
 * every attribute and sibling element the other parsers read.
 *
 * Falls back to `{ lightText: text, tins: [] }` (i.e. "use the DOM path")
 * whenever the blocks don't pair up one-to-one, which is the only structural
 * assumption made here.
 *
 * Throws if the file has TIN(s) to extract AND declares a `linearUnit` other
 * than metres — see `assertLandxmlMetres`. That is the one
 * exception to "never guess, just decline": a bad unit is a definite defect,
 * not a structural shape this module merely can't confidently handle, and
 * catching it here (from the tiny `<Units>` block) avoids silently falling
 * through to the DOM path only to pay the full multi-hundred-MB parse this
 * module exists to avoid.
 */
export function extractTins(text: TextLike): TinExtraction {
  // Only reached when this document has no (or no cleanly-paired) TIN — i.e.
  // it is not the multi-hundred-MB shape this module exists for. Materialising
  // it whole is what the DOM-fallback path has always needed to do; a huge
  // document that ALSO has no TIN in it is a pre-existing, separate limit of
  // handing anything to `DOMParser` at all, not one this module introduces.
  const declineAsIs = (): TinExtraction => ({ lightText: text.slice(0, text.length), tins: [] });
  const pntsBlocks = findBlocks(text, 'Pnts');
  const facesBlocks = findBlocks(text, 'Faces');
  if (pntsBlocks.length === 0 || pntsBlocks.length !== facesBlocks.length) {
    return declineAsIs();
  }
  // Each Definition writes <Pnts> before its <Faces>; anything else means our
  // positional pairing would be guessing, so decline the whole file.
  for (let i = 0; i < pntsBlocks.length; i++) {
    if (pntsBlocks[i].openStart > facesBlocks[i].openStart) return declineAsIs();
    if (i + 1 < pntsBlocks.length && facesBlocks[i].closeEnd > pntsBlocks[i + 1].openStart) {
      return declineAsIs();
    }
  }

  assertLandxmlMetres(text);

  const tins: RawTin[] = [];
  const pieces: string[] = [];
  let cursor = 0;

  for (let i = 0; i < pntsBlocks.length; i++) {
    const p = pntsBlocks[i];
    const f = facesBlocks[i];
    const pts = parsePnts(text, p.bodyStart, p.bodyEnd);
    const index = pts.count > 0 ? parseFaces(text, f.bodyStart, f.bodyEnd, buildIdLookup(pts.ids, pts.count, pts.maxId)) : new Uint32Array(0);

    const positions64 = pts.xyz.slice(0, pts.count * 3);
    const positions = new Float32Array(positions64.length);
    positions.set(positions64);

    tins.push({
      positions,
      positions64,
      // `subarray` still views the oversized buffer — copy so the slack is freed.
      index: new Uint32Array(index),
      vertexCount: pts.count,
      triangleCount: index.length / 3,
      zMin: Number.isFinite(pts.zMin) ? pts.zMin : 0,
      zMax: Number.isFinite(pts.zMax) ? pts.zMax : 0,
      surfaceArea: indexedArea(positions64, index),
    });

    pieces.push(text.slice(cursor, p.openStart), `<Pnts ${TIN_INDEX_ATTR}="${i}"/>`);
    pieces.push(text.slice(p.closeEnd, f.openStart), `<Faces ${TIN_INDEX_ATTR}="${i}"/>`);
    cursor = f.closeEnd;
  }
  pieces.push(text.slice(cursor));

  return { lightText: pieces.join(''), tins };
}
